import { Editor } from '@tiptap/react'
import { Bold, Italic, Code, Link2, Heading1, Heading2, Code2, Unlink } from 'lucide-react'
import { useState, useCallback } from 'react'

interface BubbleMenuToolbarProps {
  editor: Editor
  channel?: 'chat' | 'email' | 'whatsapp'
}

export default function BubbleMenuToolbar({ editor, channel }: BubbleMenuToolbarProps) {
  const isWhatsApp = channel === 'whatsapp'
  const [showLinkInput, setShowLinkInput] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')

  const setLink = useCallback(() => {
    if (!linkUrl) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      setShowLinkInput(false)
      return
    }

    // Add https:// if no protocol
    const url = linkUrl.match(/^https?:\/\//) ? linkUrl : `https://${linkUrl}`

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    setShowLinkInput(false)
    setLinkUrl('')
  }, [editor, linkUrl])

  const openLinkInput = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href || ''
    setLinkUrl(previousUrl)
    setShowLinkInput(true)
  }, [editor])

  if (showLinkInput) {
    return (
      <div className="flex items-center gap-1 bg-white rounded-lg shadow-lg border border-gray-200 p-1">
        <input
          type="url"
          placeholder="https://..."
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              setLink()
            }
            if (e.key === 'Escape') {
              setShowLinkInput(false)
              setLinkUrl('')
            }
          }}
          className="px-2 py-1 text-sm border-none outline-none w-48"
          autoFocus
        />
        <button
          type="button"
          onClick={setLink}
          className="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          OK
        </button>
        <button
          type="button"
          onClick={() => {
            setShowLinkInput(false)
            setLinkUrl('')
          }}
          className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
        >
          Cancelar
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-0.5 bg-white rounded-lg shadow-lg border border-gray-200 p-1">
      {/* Bold */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Negrito (Cmd+B)"
      >
        <Bold className="w-4 h-4" />
      </ToolbarButton>

      {/* Italic */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Itálico (Cmd+I)"
      >
        <Italic className="w-4 h-4" />
      </ToolbarButton>

      {/* Inline Code - hidden for WhatsApp */}
      {!isWhatsApp && (
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          title="Código inline"
        >
          <Code className="w-4 h-4" />
        </ToolbarButton>
      )}

      {/* Link - hidden for WhatsApp */}
      {!isWhatsApp && (
        editor.isActive('link') ? (
          <ToolbarButton
            onClick={() => editor.chain().focus().unsetLink().run()}
            isActive={true}
            title="Remover link"
          >
            <Unlink className="w-4 h-4" />
          </ToolbarButton>
        ) : (
          <ToolbarButton onClick={openLinkInput} isActive={false} title="Adicionar link">
            <Link2 className="w-4 h-4" />
          </ToolbarButton>
        )
      )}

      {!isWhatsApp && <div className="w-px h-5 bg-gray-200 mx-1" />}

      {/* Heading 1 - hidden for WhatsApp */}
      {!isWhatsApp && (
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Título 1"
        >
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>
      )}

      {/* Heading 2 - hidden for WhatsApp */}
      {!isWhatsApp && (
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Título 2"
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
      )}

      {/* Code Block - hidden for WhatsApp */}
      {!isWhatsApp && (
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          isActive={editor.isActive('codeBlock')}
          title="Bloco de código"
        >
          <Code2 className="w-4 h-4" />
        </ToolbarButton>
      )}
    </div>
  )
}

interface ToolbarButtonProps {
  onClick: () => void
  isActive: boolean
  title: string
  children: React.ReactNode
}

function ToolbarButton({ onClick, isActive, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors cursor-pointer ${
        isActive
          ? 'bg-gray-200 text-gray-900'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  )
}
